import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { clientsAPI, workstreamAPI } from '../services/api';
import LogoutConfirmModal from './LogoutConfirmModal';
import './Layout.css';
import dashboardIcon from '../assets/icons/dashboard.png';
import driversIcon from '../assets/icons/driver-main-dash.png';
import vehicleIcon from '../assets/icons/vehicle.png';
import sitesIcon from '../assets/icons/sites.png';
import clientIcon from '../assets/icons/client.png';
import teamIcon from '../assets/icons/team.png';
import groupIcon from '../assets/icons/group.png';
import clipboardIcon from '../assets/icons/clipboard.png';
import dataCollectionIcon from '../assets/icons/data-collection.png';
import myshieldLogo from '../assets/myshield-logo.png';
import myshieldNameless from '../assets/myshield-nameless.png';
import stickyNotesIcon from '../assets/icons/sticky-notes.png';
import insuranceIcon from '../assets/icons/insurance.png';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, permissions, isMTA, isFleetManager } = useAuth();
  const isOnboarder = user?.role === 'Onboarder';
  const isMtaOnly = isMTA && !isOnboarder;
  const navigate = useNavigate();
  const location = useLocation();
  const [clientLogo, setClientLogo] = useState<string | null>(null);
  const [trailersEnabled, setTrailersEnabled] = useState(false);
  const [claimsEnabled, setClaimsEnabled] = useState(false);
  const [logoutModal, setLogoutModal]     = useState<{ open: boolean; pendingCount: number }>({ open: false, pendingCount: 0 });
  const [logoutChecking, setLogoutChecking] = useState(false);
  const deploymentEnvironment = process.env.REACT_APP_DEPLOYMENT_ENVIRONMENT;
  const isNonProduction = deploymentEnvironment && deploymentEnvironment !== 'Production';

  // ── Accordion state for client-role grouped nav ───────────────────────────
  const myFeedPaths      = ['/my-feed', '/messages'];
  const myFleetPaths     = ['/dashboard', '/vehicles', '/trailers', '/drivers', '/claims'];
  const adminToolPaths   = ['/reports', '/consent-records'];

  const [openGroups, setOpenGroups] = useState({
    myFeed:       myFeedPaths.includes(location.pathname),
    myFleet:      myFleetPaths.includes(location.pathname),
    adminToolkit: adminToolPaths.includes(location.pathname),
  });

  // ── FleetManager accordion state ───────────────────────────────────────────
  const fmFeedPaths  = ['/my-feed', '/messages'];
  const fmFleetPaths = ['/dashboard', '/vehicles', '/trailers', '/drivers', '/sites', '/claims'];

  const [fmOpenGroups, setFmOpenGroups] = useState({
    feed:  fmFeedPaths.includes(location.pathname),
    fleet: fmFleetPaths.includes(location.pathname),
  });

  const toggleFm = (g: 'feed' | 'fleet') =>
    setFmOpenGroups(prev => ({ ...prev, [g]: !prev[g] }));

  useEffect(() => {
    if (!isFleetManager) return;
    setFmOpenGroups(prev => ({
      feed:  prev.feed  || fmFeedPaths.includes(location.pathname),
      fleet: prev.fleet || fmFleetPaths.includes(location.pathname),
    }));
  }, [location.pathname, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SuperAdmin dashboard group accordion ──────────────────────────────────
  const superAdminDashPaths = ['/dashboard', '/workstream', '/clients', '/insights'];
  const [dashGroupOpen, setDashGroupOpen] = useState(
    superAdminDashPaths.includes(location.pathname)
  );

  // Auto-open the relevant group when path changes
  useEffect(() => {
    if (user?.role !== 'Client') return;
    setOpenGroups(prev => ({
      myFeed:       prev.myFeed      || myFeedPaths.includes(location.pathname),
      myFleet:      prev.myFleet     || myFleetPaths.includes(location.pathname),
      adminToolkit: prev.adminToolkit || adminToolPaths.includes(location.pathname),
    }));
  }, [location.pathname, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user?.role !== 'SuperAdmin') return;
    if (superAdminDashPaths.includes(location.pathname)) setDashGroupOpen(true);
  }, [location.pathname, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (g: 'myFeed' | 'myFleet' | 'adminToolkit') =>
    setOpenGroups(prev => ({ ...prev, [g]: !prev[g] }));

  const handleLogoutClick = async () => {
    if (!permissions.canProcessWorkstream) { logout(); return; }
    setLogoutChecking(true);
    let total = 0;
    try {
      // Try the dedicated count endpoint first (covers all 5 pending types)
      const res = await workstreamAPI.getPendingCount();
      total = res.data?.total ?? 0;
    } catch {
      // Fallback: check each pending type individually in parallel
      try {
        const [pending, removals, reinstate, temp, change] = await Promise.allSettled([
          workstreamAPI.getPending(),
          workstreamAPI.getRemovalRequests(),
          workstreamAPI.getReinstatementRequests(),
          workstreamAPI.getTempCoverRequests(),
          workstreamAPI.getChangeOfVehicleRequests(),
        ]);
        const count = (r: PromiseSettledResult<any>) =>
          r.status === 'fulfilled' && Array.isArray(r.value?.data) ? r.value.data.length : 0;
        total = count(pending) + count(removals) + count(reinstate) + count(temp) + count(change);
      } catch {
        // If all checks fail, show the modal to be safe rather than silently logout
        total = 1;
      }
    } finally {
      setLogoutChecking(false);
    }
    if (total > 0) {
      setLogoutModal({ open: true, pendingCount: total });
    } else {
      logout();
    }
  };

  // Only show the client's branded logo for standard Client-role users
  useEffect(() => {
    if ((user?.role === 'Client' || isFleetManager) && user?.clientId) {
      clientsAPI.getLogo(user.clientId)
        .then((r) => setClientLogo(r.data.logoBase64 || null))
        .catch(() => {});
    }
  }, [user?.role, user?.clientId]);

  useEffect(() => {
    if ((user?.role !== 'Client' && !isFleetManager) || !user?.clientId) {
      setTrailersEnabled(false);
      setClaimsEnabled(false);
      return;
    }
    clientsAPI.getById(user.clientId)
      .then((response) => {
        setTrailersEnabled(response.data.trailersEnabled === true);
        setClaimsEnabled(response.data.claimsEnabled === true);
      })
      .catch(() => { setTrailersEnabled(false); setClaimsEnabled(false); });
  }, [user?.role, user?.clientId, isFleetManager]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
    {isNonProduction && (
      <div style={{ background: '#7c2d12', color: '#fff', textAlign: 'center', padding: '7px 12px', fontWeight: 800, fontSize: 13, letterSpacing: '0.04em' }}>
        {deploymentEnvironment.toUpperCase()} ENVIRONMENT — TEST DATA ONLY — DO NOT TREAT AS LIVE
      </div>
    )}
    <div className="layout">
      <header className="header">
        <div className="header-left">
          <img 
            src={myshieldNameless} 
            alt="Myshield" 
            className="header-logo"
          />
          <div className="header-title">
            <h1>
              <span className="fleet-text">My</span><span className="atlas-text">shield</span>
            </h1>
            <p className="header-subtitle">Policy Management By Yourshield</p>
          </div>
        </div>
        
        <div className="header-right">
          {(user?.role === 'Client' || isFleetManager) && clientLogo && (
            <img src={clientLogo} alt="Company logo" className="header-client-logo" />
          )}
          <span className="user-info">
            {user?.firstName} {user?.lastName}
            {user?.role && user.role !== 'Client' && (
              <span className="admin-badge">
                {user.role === 'SuperAdmin' ? 'Super Admin'
                  : user.role === 'SuperUser' ? 'Super User'
                  : user.role === 'FleetManager' ? 'Fleet Manager'
                  : user.role === 'FleetManagerAdmin' ? 'Fleet Manager - Admin'
                  : user.role === 'ClientAdmin' ? 'Client Admin'
                  : user.role === 'Onboarder' ? 'Onboarder'
                  : user.role}
              </span>
            )}
          </span>
          <button
            onClick={handleLogoutClick}
            disabled={logoutChecking}
            className="btn btn-secondary btn-sm"
          >
            {logoutChecking ? 'Checking…' : 'Logout'}
          </button>
        </div>
      </header>

      <div className="main-container">
        <aside className="sidebar">
          {isFleetManager ? (
            /* ── FLEET MANAGER ACCORDION NAV ─────────────────────────────────────────── */
            <nav>
              {/* ── MY FEED group ── */}
              <button
                className={`nav-group-parent ${fmFeedPaths.some(p => isActive(p)) ? 'active' : ''}`}
                onClick={() => { navigate('/my-feed'); toggleFm('feed'); }}
              >
                <img src={stickyNotesIcon} alt="" className="nav-icon" />
                My Feed
                <span className={`nav-chevron${fmOpenGroups.feed ? ' open' : ''}`}>&#8250;</span>
              </button>
              <div className={`nav-group-children${fmOpenGroups.feed ? ' open' : ''}`}>
                <button
                  className={`nav-child-item ${isActive('/messages') ? 'active' : ''}`}
                  onClick={() => navigate('/messages')}
                >
                  <img src={clipboardIcon} alt="" className="nav-child-icon" /> Notices
                </button>
              </div>

              <div className="nav-divider" />

              {/* ── MY FLEET group ── */}
              <button
                className={`nav-group-parent ${fmFleetPaths.some(p => isActive(p)) ? 'active' : ''}`}
                onClick={() => toggleFm('fleet')}
              >
                <img src={vehicleIcon} alt="" className="nav-icon" />
                My Fleet
                <span className={`nav-chevron${fmOpenGroups.fleet ? ' open' : ''}`}>&#8250;</span>
              </button>
              <div className={`nav-group-children${fmOpenGroups.fleet ? ' open' : ''}`}>
                <button
                  className={`nav-child-item ${isActive('/dashboard') ? 'active' : ''}`}
                  onClick={() => navigate('/dashboard')}
                >
                  <img src={dashboardIcon} alt="" className="nav-child-icon" /> Dashboard
                </button>
                <button
                  className={`nav-child-item ${isActive('/vehicles') ? 'active' : ''}`}
                  onClick={() => navigate('/vehicles')}
                >
                  <img src={vehicleIcon} alt="" className="nav-child-icon" /> Vehicles
                </button>
                {trailersEnabled && (
                  <button
                    className={`nav-child-item ${isActive('/trailers') ? 'active' : ''}`}
                    onClick={() => navigate('/trailers')}
                  >
                    <img src={vehicleIcon} alt="" className="nav-child-icon" /> Trailers
                  </button>
                )}
                <button
                  className={`nav-child-item ${isActive('/drivers') ? 'active' : ''}`}
                  onClick={() => navigate('/drivers')}
                >
                  <img src={driversIcon} alt="" className="nav-child-icon" /> Referred Named Drivers
                </button>
                <button
                  className={`nav-child-item ${isActive('/sites') ? 'active' : ''}`}
                  onClick={() => navigate('/sites')}
                >
                  <img src={sitesIcon} alt="" className="nav-child-icon" /> Sites
                </button>
              </div>

              {claimsEnabled && (
                <button className={`nav-item ${isActive('/claims') ? 'active' : ''}`} onClick={() => navigate('/claims')}>
                  <img src={insuranceIcon} alt="" className="nav-icon" /> Claims
                </button>
              )}
              <div className="nav-divider" />

              {/* ── USERS ── */}
              <div className="nav-section">Admin</div>
              <button
                className={`nav-item ${isActive('/users') ? 'active' : ''}`}
                onClick={() => navigate('/users')}
              >
                <img src={groupIcon} alt="" className="nav-icon" /> Users
              </button>

              <div className="nav-divider" />

              {/* ── REPORTING ── */}
              <button
                className={`nav-item ${isActive('/reports') ? 'active' : ''}`}
                onClick={() => navigate('/reports')}
              >
                <img src={dataCollectionIcon} alt="" className="nav-icon" /> Insights &amp; Reports
              </button>

              <div className="nav-divider" />

              {/* ── ACADEMY ── */}
              <button
                className={`nav-item ${isActive('/academy') ? 'active' : ''}`}
                onClick={() => navigate('/academy')}
              >
                <img src={clipboardIcon} alt="" className="nav-icon" /> Myshield Academy
                {!localStorage.getItem(`academyDone-${user?.id}`) && (
                  <span style={{ marginLeft: 6, fontSize: 13 }} title="Training required">⭐</span>
                )}
              </button>
            </nav>

          ) : user?.role === 'Client' ? (
            /* ── CLIENT GROUPED ACCORDION NAV ─────────────────────────────────────────── */
            <nav>
              {/* ── MY FEED group ── */}
              <button
                className={`nav-group-parent ${myFeedPaths.some(p => isActive(p)) ? 'active' : ''}`}
                onClick={() => { navigate('/my-feed'); toggleGroup('myFeed'); }}
              >
                <img src={stickyNotesIcon} alt="" className="nav-icon" />
                My Feed
                <span className={`nav-chevron${openGroups.myFeed ? ' open' : ''}`}>&#8250;</span>
              </button>
              <div className={`nav-group-children${openGroups.myFeed ? ' open' : ''}`}>
                <button
                  className={`nav-child-item ${isActive('/messages') ? 'active' : ''}`}
                  onClick={() => navigate('/messages')}
                >
                  <img src={clipboardIcon} alt="" className="nav-child-icon" /> My Notices
                </button>
              </div>

              <div className="nav-divider" />

              {/* ── MY FLEET group ── */}
              <button
                className={`nav-group-parent ${myFleetPaths.some(p => isActive(p)) ? 'active' : ''}`}
                onClick={() => toggleGroup('myFleet')}
              >
                <img src={vehicleIcon} alt="" className="nav-icon" />
                My Fleet
                <span className={`nav-chevron${openGroups.myFleet ? ' open' : ''}`}>&#8250;</span>
              </button>
              <div className={`nav-group-children${openGroups.myFleet ? ' open' : ''}`}>
                <button
                  className={`nav-child-item ${isActive('/dashboard') ? 'active' : ''}`}
                  onClick={() => navigate('/dashboard')}
                >
                  <img src={dashboardIcon} alt="" className="nav-child-icon" /> My Dashboard
                </button>
                {permissions.canViewVehicles && (
                  <>
                    <button
                      className={`nav-child-item ${isActive('/vehicles') ? 'active' : ''}`}
                      onClick={() => navigate('/vehicles')}
                    >
                      <img src={vehicleIcon} alt="" className="nav-child-icon" /> Vehicles
                    </button>
                    {trailersEnabled && (
                      <button
                        className={`nav-child-item ${isActive('/trailers') ? 'active' : ''}`}
                        onClick={() => navigate('/trailers')}
                      >
                        <img src={vehicleIcon} alt="" className="nav-child-icon" /> Trailers
                      </button>
                    )}
                  </>
                )}
                {permissions.canViewDrivers && (
                  <button
                    className={`nav-child-item ${isActive('/drivers') ? 'active' : ''}`}
                    onClick={() => navigate('/drivers')}
                  >
                    <img src={driversIcon} alt="" className="nav-child-icon" /> Referred Named Drivers
                  </button>
                )}
              </div>

              {claimsEnabled && (
                <button className={`nav-item ${isActive('/claims') ? 'active' : ''}`} onClick={() => navigate('/claims')}>
                  <img src={clipboardIcon} alt="" className="nav-icon" /> Claims
                </button>
              )}
              <div className="nav-divider" />

              {/* ── SITES – standalone ── */}
              {permissions.canViewSites && (
                <button
                  className={`nav-item ${isActive('/sites') ? 'active' : ''}`}
                  onClick={() => navigate('/sites')}
                >
                  <img src={sitesIcon} alt="" className="nav-icon" /> Sites
                </button>
              )}

              <div className="nav-divider" />

              {/* ── ADMIN TOOLKIT group ── */}
              <button
                className={`nav-group-parent ${adminToolPaths.some(p => isActive(p)) ? 'active' : ''}`}
                onClick={() => toggleGroup('adminToolkit')}
              >
                <img src={dataCollectionIcon} alt="" className="nav-icon" />
                Admin Toolkit
                <span className={`nav-chevron${openGroups.adminToolkit ? ' open' : ''}`}>&#8250;</span>
              </button>
              <div className={`nav-group-children${openGroups.adminToolkit ? ' open' : ''}`}>
                {permissions.canViewReports && (
                  <button
                    className={`nav-child-item ${isActive('/reports') ? 'active' : ''}`}
                    onClick={() => navigate('/reports')}
                  >
                    <img src={dataCollectionIcon} alt="" className="nav-child-icon" /> Insights &amp; Reports
                  </button>
                )}
              {permissions.canViewConsentRecords && (
                <button
                  className={`nav-child-item ${isActive('/consent-records') ? 'active' : ''}`}
                  onClick={() => navigate('/consent-records')}
                >
                  <img src={dataCollectionIcon} alt="" className="nav-child-icon" /> Consent Records
                </button>
              )}
            </div>

            <div className="nav-divider" />

            {/* ── MYSHIELD ACADEMY ── */}
            <button
              className={`nav-item ${isActive('/academy') ? 'active' : ''}`}
              onClick={() => navigate('/academy')}
            >
              <img src={clipboardIcon} alt="" className="nav-icon" /> Myshield Academy
              {!localStorage.getItem(`academyDone-${user?.id}`) && (
                <span style={{ marginLeft: 6, fontSize: 13 }} title="Training required">⭐</span>
              )}
            </button>

          </nav>
        ) : (
            /* ── NON-CLIENT FLAT NAV ─────────────────────────────────────────── */
            <nav>
              {/* My Feed – visible to all */}
              <button
                className={`nav-item ${isActive('/my-feed') ? 'active' : ''}`}
                onClick={() => navigate('/my-feed')}
              >
                <img src={stickyNotesIcon} alt="" className="nav-icon" /> My Feed
              </button>
              {permissions.canViewClaims && (
                <button className={`nav-item ${isActive('/claims') ? 'active' : ''}`} onClick={() => navigate('/claims')}>
                  <img src={clipboardIcon} alt="" className="nav-icon" /> Claims
                </button>
              )}

              {/* Divider between My Feed and Dashboard/Workstream */}
              <div className="nav-divider" />

              {/* Dashboard accordion for SuperAdmin — contains Workstream, Clients, Insights */}
              {user?.role === 'SuperAdmin' ? (
                <>
                  <button
                    className={`nav-group-parent ${
                      superAdminDashPaths.some(p => isActive(p)) ? 'active' : ''
                    }`}
                    onClick={() => { navigate('/dashboard'); setDashGroupOpen(prev => !prev); }}
                  >
                    <img src={dashboardIcon} alt="" className="nav-icon" />
                    Dashboard
                    <span className={`nav-chevron${dashGroupOpen ? ' open' : ''}`}>&#8250;</span>
                  </button>
                  <div className={`nav-group-children${dashGroupOpen ? ' open' : ''}`}>
                    {permissions.canViewWorkstream && (
                      <button
                        className={`nav-child-item ${isActive('/workstream') ? 'active' : ''}`}
                        onClick={() => navigate('/workstream')}
                      >
                        <img src={teamIcon} alt="" className="nav-child-icon" /> Workstream
                      </button>
                    )}
                    {permissions.canViewClients && (
                      <button
                        className={`nav-child-item ${isActive('/clients') ? 'active' : ''}`}
                        onClick={() => navigate('/clients')}
                      >
                        <img src={clientIcon} alt="" className="nav-child-icon" /> Clients
                      </button>
                    )}
                    {permissions.canViewInsights && (
                      <button
                        className={`nav-child-item ${isActive('/insights') ? 'active' : ''}`}
                        onClick={() => navigate('/insights')}
                      >
                        <img src={dataCollectionIcon} alt="" className="nav-child-icon" /> Insights &amp; Reporting
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Dashboard – flat for non-SuperAdmin roles; hidden for MTA (they work from Workstream) */}
                  {!isMtaOnly && (
                  <button
                    className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}
                    onClick={() => navigate('/dashboard')}
                  >
                    <img src={dashboardIcon} alt="" className="nav-icon" /> Dashboard
                  </button>
                  )}

                  {permissions.canViewWorkstream && (
                    <>
                      {/* No divider before Workstream for MTA — it's the first item after My Feed */}
                      {!isMtaOnly && <div className="nav-divider" />}
                      <button
                        className={`nav-item ${isActive('/workstream') ? 'active' : ''}`}
                        onClick={() => navigate('/workstream')}
                      >
                        <img src={teamIcon} alt="" className="nav-icon" /> Workstream
                      </button>
                    </>
                  )}

                  {permissions.canViewClients && (
                    <>
                      <div className="nav-divider" />
                      <button
                        className={`nav-item ${isActive('/clients') ? 'active' : ''}`}
                        onClick={() => navigate('/clients')}
                      >
                        <img src={clientIcon} alt="" className="nav-icon" /> Clients
                      </button>
                    </>
                  )}

                  {permissions.canViewInsights && (
                    <>
                      <div className="nav-divider" />
                      <button
                        className={`nav-item ${isActive('/insights') ? 'active' : ''}`}
                        onClick={() => navigate('/insights')}
                      >
                        <img src={dataCollectionIcon} alt="" className="nav-icon" /> Insights &amp; Reporting
                      </button>
                    </>
                  )}
                </>
              )}

              {permissions.canViewVehicles && (
                <>
                  <div className="nav-divider" />
                  <button
                    className={`nav-item ${isActive('/vehicles') ? 'active' : ''}`}
                    onClick={() => navigate('/vehicles')}
                  >
                    <img src={vehicleIcon} alt="" className="nav-icon" /> Vehicles
                  </button>
                </>
              )}

              {permissions.canViewDrivers && (
                <>
                  <div className="nav-divider" />
                  <button
                    className={`nav-item ${isActive('/drivers') ? 'active' : ''}`}
                    onClick={() => navigate('/drivers')}
                  >
                    <img src={driversIcon} alt="" className="nav-icon" /> Referred Named Drivers
                  </button>
                </>
              )}

              {permissions.canViewSites && (
                <>
                  <div className="nav-divider" />
                  <button
                    className={`nav-item ${isActive('/sites') ? 'active' : ''}`}
                    onClick={() => navigate('/sites')}
                  >
                    <img src={sitesIcon} alt="" className="nav-icon" /> Sites
                  </button>
                </>
              )}

              {permissions.canViewMessages && (
                <>
                  <div className="nav-divider" />
                  <button
                    className={`nav-item ${isActive('/messages') ? 'active' : ''}`}
                    onClick={() => navigate('/messages')}
                  >
                    <img src={clipboardIcon} alt="" className="nav-icon" /> Messages
                  </button>
                </>
              )}

              {permissions.canViewReports && (
                <>
                  <div className="nav-divider" />
                  <button
                    className={`nav-item ${isActive('/reports') ? 'active' : ''}`}
                    onClick={() => navigate('/reports')}
                  >
                    <img src={dataCollectionIcon} alt="" className="nav-icon" /> Insights &amp; Reports
                  </button>
                </>
              )}

              {(permissions.canViewUsers || permissions.canViewAdminTools || permissions.canViewConsentRecords) && (
                <>
                  <div className="nav-divider" />
                  <div className="nav-section">Admin</div>

                  {permissions.canViewUsers && (
                    <button
                      className={`nav-item ${isActive('/users') ? 'active' : ''}`}
                      onClick={() => navigate('/users')}
                    >
                      <img src={groupIcon} alt="" className="nav-icon" /> Users
                    </button>
                  )}

                  {permissions.canViewAdminTools && (
                    <>
                      {permissions.canViewUsers && <div className="nav-divider" />}
                      <button
                        className={`nav-item ${isActive('/admin-tools') ? 'active' : ''}`}
                        onClick={() => navigate('/admin-tools')}
                      >
                        <img src={clipboardIcon} alt="" className="nav-icon" /> Admin Tools
                      </button>
                      {permissions.canViewConsentRecords && (
                        <button
                          className={`nav-child-item ${isActive('/consent-records') ? 'active' : ''}`}
                          onClick={() => navigate('/consent-records')}
                        >
                          <img src={dataCollectionIcon} alt="" className="nav-child-icon" /> Consent Records
                        </button>
                      )}
                    </>
                  )}
                </>
              )}

              {(user?.role === 'SuperAdmin' || user?.role === 'SuperUser') && (
                <>
                  <div className="nav-divider" />
                  <button
                    className={`nav-item ${isActive('/security-compliance') ? 'active' : ''}`}
                    onClick={() => navigate('/security-compliance')}
                  >
                    <img src={clipboardIcon} alt="" className="nav-icon" /> Security &amp; Compliance
                  </button>
                </>
              )}

              <div className="nav-divider" />
              <button
                className={`nav-item ${isActive('/academy') ? 'active' : ''}`}
                onClick={() => navigate('/academy')}
              >
                <img src={dataCollectionIcon} alt="" className="nav-icon" /> Myshield Academy
              </button>
            </nav>
          )}

        </aside>

        <main className="content">
          {children}
        </main>
      </div>

      <footer className="compliance-footer">
        <span className="compliance-brand">Myshield &mdash; A trading name of Yourshield Ltd</span>
        <span className="compliance-sep">|</span>
        <span>Lancaster House, Moorfield Close, Yeadon, Leeds, LS19 7YAL</span>
        <span className="compliance-sep">|</span>
        <span>FRN: 617848</span>
        <span className="compliance-sep">|</span>
        <span>Company No: 08693210</span>
        <span className="compliance-sep">|</span>
        <a
          href="https://www.yourshield.co.uk/complaints/"
          target="_blank"
          rel="noopener noreferrer"
          className="compliance-link"
        >
          Complaints Procedure
        </a>
        <span className="compliance-sep">|</span>
        <a
          href="https://www.yourshield.co.uk/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          className="compliance-link"
        >
          Privacy Policy
        </a>
        <span className="compliance-sep">|</span>
        <a
          href="https://www.yourshield.co.uk/terms-of-business/"
          target="_blank"
          rel="noopener noreferrer"
          className="compliance-link"
        >
          Terms of Business
        </a>
      </footer>
    </div>

    {logoutModal.open && (
      <LogoutConfirmModal
        pendingCount={logoutModal.pendingCount}
        onConfirm={() => {
          setLogoutModal({ open: false, pendingCount: 0 });
          logout();
        }}
        onGoToWorkstream={() => {
          setLogoutModal({ open: false, pendingCount: 0 });
          navigate('/workstream');
        }}
      />
    )}
    </>
  );
};

export default Layout;
